import request from "./request";
import type {
  AircraftOverviewResponse,
  ComplianceWithCoursesResponse,
  CourseCertificateWithActions,
} from "../shared/api-types/certificate";
import type { CachedResponse } from "../shared/api-types/cache";

type HydratableCourseCertificate = Omit<Partial<CourseCertificate>, "refreshers"> & {
  refreshers?: Array<number | CourseCertificate>;
};
type HydratableCertificateMap = Record<string | number, HydratableCourseCertificate>;

export type CourseCertificate = Omit<CourseCertificateWithActions, "refreshers"> & {
  courseId?: string;
  refreshers?: CourseCertificate[];
  hideGreenCoursePlayAction?: boolean;
};

export type CertificateMap = Record<string | number, CourseCertificate>;

export type ComplianceWithCoursesResponseHydrated = Omit<ComplianceWithCoursesResponse, "certificateMap"> & {
  certificateMap: CertificateMap;
};

const GREEN_CERTIFICATE_STATUSES = ["ok", "never_expires"];

const isGreenCourseAggregate = (cert: CourseCertificate): boolean => {
  const status = cert.actions?.downloadableCertificate?.status ?? cert.certificate?.status ?? "";
  return GREEN_CERTIFICATE_STATUSES.includes(status);
};

const markGreenCoursePlayActionsHidden = (certificateMap: CertificateMap): void => {
  Object.values(certificateMap).forEach((cert) => {
    if (!isGreenCourseAggregate(cert)) {
      return;
    }

    cert.hideGreenCoursePlayAction = true;
  });
};

const hydrateCertificateMap = (certificateMap?: HydratableCertificateMap): void => {
  if (!certificateMap) {
    return;
  }

  Object.values(certificateMap).forEach((cert) => {
    if (cert.refreshers && Array.isArray(cert.refreshers)) {
      (cert as CourseCertificate).refreshers = cert.refreshers
        .map((refresherId: any) => {
          // If it's already an object, keep it
          if (typeof refresherId === "object") {
            return refresherId;
          }
          // Otherwise, look up the full certificate object from the map
          return certificateMap[refresherId] || certificateMap[`cc_${refresherId}`];
        })
        .filter(Boolean); // Remove any undefined entries
    }
  });

  markGreenCoursePlayActionsHidden(certificateMap as Record<string | number, CourseCertificate>);
};

export const getUserCompliance = async (
  crewId: number,
  userId: number,
  useCache: boolean = true,
): Promise<CachedResponse<ComplianceWithCoursesResponseHydrated>> => {
  const url = `/compliance/crews/${crewId}/users/${userId}/courses${useCache ? "" : "?useCache=false"}`;
  const json = await request(url);
  const response = json as CachedResponse<ComplianceWithCoursesResponse>;

  // Hydrate refreshers: replace IDs with full certificate objects
  hydrateCertificateMap(response.data.certificateMap);

  // After hydration, refreshers are now full objects instead of IDs
  return response as CachedResponse<ComplianceWithCoursesResponseHydrated>;
};

/**
 * Get batch compliance data for all users in a crew with aircraft (OPTIMIZED - single request)
 * Returns data for all crew members in one request instead of N individual requests
 *
 * @param crewId - The crew ID
 * @param useCache - Whether to use cached data (default: true)
 * @param baseIds - Optional base IDs to filter users by specific bases
 * @returns CachedResponse with Record of userId -> ComplianceWithCoursesResponse
 */
export const getComplianceWithCourses = async (
  crewId: number,
  useCache: boolean = true,
  baseIds?: number[] | null,
): Promise<CachedResponse<Record<string, ComplianceWithCoursesResponseHydrated>>> => {
  const params = new URLSearchParams();
  if (!useCache) params.append("useCache", "false");
  baseIds?.forEach((id) => params.append("baseIds", String(id)));
  const queryString = params.toString();
  const url = `/compliance/crews/${crewId}/users/courses${queryString ? `?${queryString}` : ""}`;
  const json = await request(url);
  const response = json as CachedResponse<Record<string, ComplianceWithCoursesResponse>>;

  // Hydrate refreshers for each user's compliance data
  if (response.data) {
    Object.values(response.data).forEach((userCompliance) => {
      hydrateCertificateMap(userCompliance.certificateMap);
    });
  }

  // After hydration, refreshers are now full objects instead of IDs
  return response as CachedResponse<Record<string, ComplianceWithCoursesResponseHydrated>>;
};

export const getAircraftOverview = async (
  crewId: number,
  baseIds?: number[] | null,
  userId?: number,
): Promise<CachedResponse<AircraftOverviewResponse>> => {
  const params = new URLSearchParams();
  baseIds?.forEach((id) => params.append("baseIds", String(id)));
  if (userId) params.append("userId", String(userId));
  const queryString = params.toString();
  return request(`/compliance/crews/${crewId}/aircraft-overview${queryString ? `?${queryString}` : ""}`);
};
